import {
  addDays,
  endOfDay,
  startOfDay,
} from 'date-fns';
import { DurationUnit } from 'src/common/types/api/listings';
import { FULL_DAY_HOURS } from 'src/listing/constants';
import {
  getMinMaxHoursForPreferredPriceTier,
  UnitPriceDetail,
} from 'src/listing/helper';

export type TripCartDurationOption = {
  duration: number;
  unit: DurationUnit;
  isInstabook: boolean;
  durationFormatted: string; // in PT{number}H
};

export const formatDurationUnit = (
  duration: number,
  unit: DurationUnit,
): string => {
  if (unit === 'hour') {
    return `PT${duration}H`;
  }

  // Night is formatted the same as day for API, but still represents an extra day
  return `PT${duration * FULL_DAY_HOURS}H`;
};

const getOption = (
  duration: number,
  unit: DurationUnit,
): TripCartDurationOption => ({
  duration,
  unit,
  isInstabook: false,
  durationFormatted: formatDurationUnit(duration, unit),
});

/**
 * Always show full day on same day i.e. move the multiday duration over to same day
 */
export const hasFullDay = (prices: UnitPriceDetail): boolean => {
  if (!prices.multiday?.length) return false;

  return prices.multiday.some((tier) => tier.price_unit === 'day' && tier.min_unit_quantity === 1);
};

// Similar to listing/helper#getDurationOptions, but does not do translations
export const getDurationOptions = (
  unitPriceDetails: UnitPriceDetail,
  isMultiDay: boolean,
): TripCartDurationOption[] => {
  const tierType: keyof UnitPriceDetail = isMultiDay ? 'multiday' : 'hour';
  let tiers = unitPriceDetails?.[tierType];

  if (!tiers) {
    // Invalid listing
    return [];
  }

  const includesFullDay = hasFullDay(unitPriceDetails);
  // Manually set the tier data for 'hour' as that does not exist on unitPriceDetails
  // when pricing is only in days. We want to move '1 day' to hourly duration option.
  // The remaining days will be kept multiday
  if (!isMultiDay && includesFullDay && !tiers.length) {
    // Will exist due to above check `hasFullDay`
    const dayItem = unitPriceDetails.multiday![0];

    tiers = [{
      currency: dayItem.currency,
      min_unit_quantity: FULL_DAY_HOURS,
      max_unit_quantity: FULL_DAY_HOURS,
      price_unit: 'hour',
      value: dayItem.value,
    }];
  }

  const minMaxHours = getMinMaxHoursForPreferredPriceTier(tiers);

  // Config error
  if (!minMaxHours) return [];

  const { min: minHours, max: maxHours } = minMaxHours;
  const hourDivisor = tierType === 'multiday' ? FULL_DAY_HOURS : 1;
  const count = (maxHours - minHours) / hourDivisor + 1;
  const min = minHours / hourDivisor;
  const unit = tiers[0].price_unit as DurationUnit;

  const options: TripCartDurationOption[] = Array.from({ length: count }, (_, i) => {
    const duration = i + min;
    return getOption(duration, unit);
  });

  const hasMixedPricing = unitPriceDetails && (
    (
      unitPriceDetails.hour?.length
      && unitPriceDetails.multiday?.length
    )
    || includesFullDay
  );

  if (!hasMixedPricing) {
    return options;
  }

  if (isMultiDay) {
    // Remove "1 day" option for multiday
    if (unit === 'day' && includesFullDay) {
      options.shift();
    }
    return options;
  }

  // Same day. Add "Full day" option
  if (includesFullDay) {
    const lastIndex = options.findLastIndex((option) => option.duration === FULL_DAY_HOURS);
    if (lastIndex === options.length - 1) {
      // Remove option configured from '24 hours' and then reset to 1 day
      options.pop();
    }
    // '1 Day' option is removed from multi-day array,
    // it's safe to add option to hours array
    options.push(getOption(1, 'day'));
  }
  return options;
};

// Similar to TripDetailsOld#calculateUpdatedDateRange
export const getDateRangeForDuration = (
  startDate: Date,
  duration: number,
  unit: DurationUnit,
): Date[] => {
  if (unit === 'hour') return [startDate];

  // Will add a full day
  let days = duration;

  // Nights extend by a day, e.g. one night is 2 days. One day has same start/end date
  if (unit === 'day') {
    days -= 1;
  }

  // if days is zero or less than zero, return the start date only.
  if (days <= 0) {
    return [startDate];
  }

  const end = addDays(startDate, days);
  // update end date to match duration
  return [startOfDay(startDate), endOfDay(end)];
};
